import type { HeldRegistration } from '@repo/desktop-bridge';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import { IconAlertTriangle, IconArrowsExchange } from '@tabler/icons-react';
import { useId, useState } from 'react';

import { tillPlate } from './TillFrame';

/**
 * Move the counter to another outlet. Resolves with the words for a refusal,
 * or `null` once the counter has moved.
 */
export type SwitchOutlet = (id: number) => Promise<string | null>;

/** The name a registration goes by on a till: the outlet's where known, else its prefix. */
const outletLabel = (outlet: HeldRegistration): string =>
  outlet.outletName ?? outlet.terminal.prefix;

/**
 * The other outlets this machine sells for, one tap each — issue #243.
 *
 * On the sign-on screen and in place of a withdrawn counter it is the whole
 * switch; mid-shift it is the body of {@link OutletSwitchDialog}. Whoever is
 * signed in is carried across by the main process where the outlet chosen has
 * them on its roster (#254), and refused where it does not — which is said
 * here, under the buttons, with the counter left where it was.
 *
 * Outline buttons at the touch floor: the one accent on a till belongs to the
 * action that takes money or starts a shift, and switching outlets is neither.
 * Draws nothing when there is nothing to switch to, which is nine tills in ten.
 */
export function OutletSwitch({
  outlets,
  onSwitch,
  layout = 'column',
}: {
  /** The registrations this machine could sell under instead — live, and not the active one. */
  outlets: readonly HeldRegistration[];
  onSwitch: SwitchOutlet;
  /**
   * A full-width list, or a row that wraps (#312). The caller's to choose: the
   * sign-on screen puts it under *Start shift*, where height is what runs out,
   * and the dialog and the withdrawn screen keep the list.
   */
  layout?: 'column' | 'row';
}) {
  const label = useId();
  // One switch at a time: a second tap while the first is answering would ask
  // the main process to move a counter that is already moving.
  const [pending, setPending] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  if (outlets.length === 0) return null;

  return (
    <div>
      <p id={label} className={tillPlate}>
        Switch to
      </p>

      <div
        role="group"
        aria-labelledby={label}
        className={`mt-2 flex gap-2 ${layout === 'row' ? 'flex-row flex-wrap' : 'flex-col'}`}
      >
        {outlets.map((outlet) => (
          <Button
            key={outlet.id}
            variant="outline"
            size="touch"
            // In a row each button is as wide as its name up to 12rem, with
            // less side padding than the list: measured at 1000x625, four
            // outlets at the full padding took two rows and scrolled the
            // sign-on screen by 66px; capped, they take one and it fits (#312).
            className={layout === 'row' ? 'max-w-48 justify-start px-5' : 'w-full justify-start'}
            disabled={pending}
            onClick={() => {
              setPending(true);
              setRefusal(null);
              void onSwitch(outlet.id).then((refused) => {
                // Moved: this screen is about to be replaced by the outlet's own.
                if (refused === null) return;

                setRefusal(refused);
                setPending(false);
              });
            }}
          >
            {/* The outlet's name where the back office has told the device one,
                and its terminal prefix where it has not — the one name every
                registration is guaranteed to carry. Truncated rather than
                wrapped: a touch target that grows a line for one long name is a
                column of buttons that no longer lines up. */}
            <span className="truncate">{outletLabel(outlet)}</span>
          </Button>
        ))}
      </div>

      {refusal !== null && (
        <Alert variant="destructive" className="mt-3">
          <IconAlertTriangle />
          <AlertDescription>{refusal}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}

/**
 * The switch on the selling screens, beside the plate that names the outlet —
 * issue #254, a counter that sells for several outlets and changes between
 * customers.
 *
 * **Two taps, and never one.** A sale made at the wrong outlet is only put right
 * by a refund, so the control that moves the counter opens a question rather
 * than answering it: which outlet, and what happens — the cashier stays signed
 * in, and a basket not yet paid for is cleared, because it was priced from the
 * outlet being left. It sits with the plate, at the far end of the header from
 * the shift controls and above the datum from every sale control, so a hand
 * reaching for a plate or *Take payment* does not find it.
 *
 * The plate itself stays a marker and not a control (`docs/design-language.md`,
 * "The outlet plate"): a marker that looked tappable would be tapped.
 */
export function OutletSwitchDialog({
  current,
  cashier,
  outlets,
  onSwitch,
}: {
  /** The outlet the counter is selling for now, as the plate names it. */
  current: string;
  /** Who is signed in, for the sentence that says they stay so. */
  cashier: string;
  outlets: readonly HeldRegistration[];
  onSwitch: SwitchOutlet;
}) {
  const [open, setOpen] = useState(false);

  if (outlets.length === 0) return null;

  return (
    <>
      {/* **Icon alone below `lg`, which is the terminal's own ~1000px window.**
          Measured there on the shop counter, whose header also carries the queue
          count, *Today's sales* and *End shift*: with its label this took 200px
          and the plate truncated to "BOA…" — the one thing on the screen that
          must never be cut short. The square keeps the touch floor and the
          label stays the button's name. */}
      <Button
        variant="outline"
        size="touch"
        className="shrink-0 max-lg:size-touch max-lg:px-0"
        onClick={() => {
          setOpen(true);
        }}
      >
        <IconArrowsExchange />
        <span className="max-lg:sr-only">Switch outlet</span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Switch outlet</DialogTitle>
            <DialogDescription>
              You stay signed in as {cashier}. Anything rung up and not yet paid for is cleared, and
              every sale after the switch is recorded for the outlet you choose.
            </DialogDescription>
          </DialogHeader>

          {/* A child, so closing unmounts it: a refusal from the last time the
              dialog was open is not still showing the next time. */}
          {open && <OutletSwitch outlets={outlets} onSwitch={onSwitch} />}

          <DialogFooter>
            <Button
              variant="outline"
              size="touch"
              onClick={() => {
                setOpen(false);
              }}
            >
              Stay at {current}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
